import { useCallback, useEffect, useRef, useState } from 'react';
import {
  canWrite,
  departmentLabel,
  errorMessage,
  financeDelete,
  financeFileUrl,
  financeGet,
  financeSend,
  financeUpload,
  isManager,
  PAYMENT_METHODS,
  todayValue,
  useFinanceCodes,
  withCurrent,
  won,
  type FinanceCtx,
} from '../financeApi';
import PrintPortal from '../common/PrintPortal';
import ExpenseRequestPrint from './ExpenseRequestPrint';
import { STATUS_INFO, type ExpenseRequest, type RequestStatus } from './expenseTypes';

const FILTERS: { key: string; label: string }[] = [
  { key: 'open', label: '진행 중' },
  { key: 'requested', label: '부서장 승인 대기' },
  { key: 'dept_approved', label: '재정부 승인 대기' },
  { key: 'finance_approved', label: '지급 대기' },
  { key: 'paid', label: '지급 완료' },
  { key: 'rejected', label: '반려' },
  { key: '', label: '전체' },
];

const STEPS: { status: RequestStatus; label: string }[] = [
  { status: 'requested', label: '청구' },
  { status: 'dept_approved', label: '부서장 승인' },
  { status: 'finance_approved', label: '재정부 승인' },
  { status: 'paid', label: '지급 완료' },
];

type Form = {
  requestDate: string;
  department: string;
  category: string;
  title: string;
  amount: string;
  requesterName: string;
  payee: string;
  payMethod: string;
  bankAccount: string;
  description: string;
};

function Stepper({ request }: { request: ExpenseRequest }) {
  const reached = request.status === 'rejected' ? -1 : STEPS.findIndex((s) => s.status === request.status);
  return (
    <div className="fin-stepper">
      {STEPS.map((step, i) => (
        <div key={step.status} className={`fin-stepper-step${i <= reached ? ' is-done' : ''}${i === reached + 1 && request.status !== 'rejected' && request.status !== 'paid' ? ' is-next' : ''}`}>
          <i>{i <= reached ? '✓' : i + 1}</i>
          <span>{step.label}</span>
        </div>
      ))}
    </div>
  );
}

// 지출 결의서(지출 청구): 부서 청구 → 부서장 승인 → 재정부 승인(재정부장) → 지급 완료(지출 내역 자동 기록).
export default function ExpenseRequests({ ctx }: { ctx: FinanceCtx }) {
  const { expenseCategories, departments } = useFinanceCodes(ctx);
  const writable = canWrite(ctx);
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [filter, setFilter] = useState('open');
  const [rows, setRows] = useState<ExpenseRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [detail, setDetail] = useState<ExpenseRequest | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [printing, setPrinting] = useState<ExpenseRequest | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const fetchRows = useCallback(() => {
    setLoading(true);
    setError(null);
    financeGet<ExpenseRequest[]>(ctx, '/expense-requests', { status: filter || undefined, year })
      .then((data) => setRows(data ?? []))
      .catch((err) => setError(errorMessage(err, '결의서를 불러오지 못했습니다.')))
      .finally(() => setLoading(false));
  }, [ctx, filter, year]);

  useEffect(fetchRows, [fetchRows]);

  const openDetail = (id: number) => {
    setActionError(null);
    financeGet<ExpenseRequest>(ctx, `/expense-requests/${id}`)
      .then(setDetail)
      .catch((err) => window.alert(errorMessage(err, '결의서를 불러오지 못했습니다.')));
  };

  const openCreate = () => {
    setEditingId(null);
    setPendingFiles([]);
    setFormError(null);
    setForm({
      requestDate: todayValue(),
      department: departments[0] ?? '',
      category: expenseCategories[0] ?? '',
      title: '',
      amount: '',
      requesterName: '',
      payee: '',
      payMethod: '계좌이체',
      bankAccount: '',
      description: '',
    });
  };

  const openEdit = (r: ExpenseRequest) => {
    setDetail(null);
    setEditingId(r.id);
    setPendingFiles([]);
    setFormError(null);
    setForm({
      requestDate: r.requestDate.slice(0, 10),
      department: r.department,
      category: r.category,
      title: r.title,
      amount: String(r.amount),
      requesterName: r.requesterName,
      payee: r.payee,
      payMethod: r.payMethod,
      bankAccount: r.bankAccount,
      description: r.description,
    });
  };

  const submitForm = async () => {
    if (!form) return;
    const amount = Number(form.amount);
    const category = form.category || expenseCategories[0] || '';
    if (!form.title.trim() || !category || !(amount > 0) || !form.requestDate) {
      setFormError('청구일·계정과목·제목·금액을 확인해 주세요.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const payload = { ...form, category, amount };
      let id = editingId;
      if (editingId) await financeSend(ctx, 'put', `/expense-requests/${editingId}`, payload);
      else id = (await financeSend<{ id: number }>(ctx, 'post', '/expense-requests', payload)).id;
      if (id && pendingFiles.length > 0) await financeUpload(ctx, `/expense-requests/${id}/attachments`, pendingFiles);
      setForm(null);
      fetchRows();
      if (id) openDetail(id);
    } catch (err) {
      setFormError(errorMessage(err, '저장하지 못했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  const act = async (path: string, body: Record<string, unknown> = {}) => {
    if (!detail) return false;
    setActionError(null);
    try {
      await financeSend(ctx, 'post', `/expense-requests/${detail.id}${path}`, body);
      openDetail(detail.id);
      fetchRows();
      return true;
    } catch (err) {
      setActionError(errorMessage(err, '처리하지 못했습니다.'));
      return false;
    }
  };

  const deptApprove = () => {
    const approverName = window.prompt('승인한 부서장 이름을 입력해 주세요.', '');
    if (approverName?.trim()) act('/dept-approve', { approverName: approverName.trim() });
  };

  const reject = () => {
    const reason = window.prompt('반려 사유를 입력해 주세요.', '');
    if (reason?.trim()) act('/reject', { reason: reason.trim() });
  };

  const [payForm, setPayForm] = useState<{ paidDate: string; method: string } | null>(null);

  const removeRequest = () => {
    if (!detail || !window.confirm('이 결의서를 삭제할까요? 첨부한 증빙도 함께 삭제됩니다.')) return;
    financeDelete(ctx, `/expense-requests/${detail.id}`)
      .then(() => {
        setDetail(null);
        fetchRows();
      })
      .catch((err) => setActionError(errorMessage(err, '삭제하지 못했습니다.')));
  };

  const uploadMore = (files: FileList | null) => {
    if (!detail || !files || files.length === 0) return;
    financeUpload(ctx, `/expense-requests/${detail.id}/attachments`, Array.from(files))
      .then(() => {
        openDetail(detail.id);
        fetchRows();
      })
      .catch((err) => setActionError(errorMessage(err, '증빙을 첨부하지 못했습니다.')))
      .finally(() => {
        if (fileRef.current) fileRef.current.value = '';
      });
  };

  const removeAttachment = (fileId: number) => {
    if (!detail || !window.confirm('이 증빙을 삭제할까요?')) return;
    financeDelete(ctx, `/expense-requests/attachments/${fileId}`)
      .then(() => openDetail(detail.id))
      .catch((err) => setActionError(errorMessage(err, '삭제하지 못했습니다.')));
  };

  const total = rows.reduce((s, r) => s + r.amount, 0);

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">지출 결의서</h2>
          <p className="manage-panel-desc">
            부서의 지출 청구를 등록하고 증빙 영수증을 첨부합니다. 부서장 승인 → 재정부 승인(재정부장) → 지급 완료 순서로 처리되며,
            지급 완료 시 지출 내역에 자동으로 기록됩니다.
          </p>
        </div>
        {writable && <button type="button" className="manage-btn manage-btn--primary" onClick={openCreate}>+ 결의서 작성</button>}
      </div>

      <div className="manage-filterbar">
        <div className="manage-seg">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" className={`manage-seg-btn${filter === f.key ? ' is-active' : ''}`} onClick={() => setFilter(f.key)}>
              {f.label}
            </button>
          ))}
        </div>
        <select className="fin-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {Array.from({ length: 5 }, (_, i) => thisYear + 1 - i).map((y) => <option key={y} value={y}>{y}년</option>)}
        </select>
        <span className="fin-muted">{rows.length}건 · {won(total)}</span>
      </div>

      {error && <div className="manage-state manage-state--error">{error}</div>}

      {!error && (
        <div className="manage-card-block">
          <div className="manage-table-wrap">
            <table className="manage-table">
              <thead>
                <tr>
                  <th style={{ width: 96 }}>청구일</th>
                  <th style={{ width: 90 }}>부서</th>
                  <th>제목</th>
                  <th style={{ width: 100 }}>계정과목</th>
                  <th className="fin-num" style={{ width: 120 }}>금액</th>
                  <th style={{ width: 60 }}>증빙</th>
                  <th style={{ width: 120 }}>상태</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7} className="manage-table-empty">불러오는 중입니다...</td></tr>}
                {!loading && rows.length === 0 && <tr><td colSpan={7} className="manage-table-empty">결의서가 없습니다.</td></tr>}
                {!loading && rows.map((r) => (
                  <tr key={r.id} className="fin-row-click" onClick={() => openDetail(r.id)}>
                    <td className="manage-table-meta">{r.requestDate.slice(0, 10)}</td>
                    <td>{departmentLabel(r.department)}</td>
                    <td><b>{r.title}</b>{r.requesterName && <span className="fin-muted"> · {r.requesterName}</span>}</td>
                    <td><span className="manage-badge manage-badge--blue">{r.category}</span></td>
                    <td className="fin-num manage-amt-out">{won(r.amount)}</td>
                    <td>{r.attachmentCount ? `📎 ${r.attachmentCount}` : <span className="fin-muted">-</span>}</td>
                    <td><span className={`manage-badge ${STATUS_INFO[r.status].badge}`}>{STATUS_INFO[r.status].label}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detail && (
        <div className="manage-modal-overlay" onClick={() => setDetail(null)}>
          <div className="manage-modal fin-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="manage-modal-header">
              <h3>지출 결의서 #{detail.id}</h3>
              <button type="button" className="manage-modal-close" onClick={() => setDetail(null)}>×</button>
            </div>
            <div className="manage-modal-body">
              {detail.status === 'rejected' ? (
                <div className="fin-inline-error">반려됨 · {detail.rejectReason}</div>
              ) : (
                <Stepper request={detail} />
              )}
              <dl className="fin-dl">
                <dt>제목</dt><dd><b>{detail.title}</b></dd>
                <dt>금액</dt><dd className="manage-amt-out">{won(detail.amount)}</dd>
                <dt>부서 / 계정과목</dt><dd>{departmentLabel(detail.department)} / {detail.category}</dd>
                <dt>청구일 / 청구자</dt><dd>{detail.requestDate.slice(0, 10)} / {detail.requesterName || '-'}</dd>
                <dt>지급처</dt><dd>{detail.payee || '-'}{detail.payMethod && ` · ${detail.payMethod}`}{detail.bankAccount && ` · ${detail.bankAccount}`}</dd>
                <dt>내용</dt><dd className="fin-pre">{detail.description || '-'}</dd>
                {detail.deptApprovedAt && (<><dt>부서장 승인</dt><dd>{detail.deptApproverName} · {detail.deptApprovedAt.slice(0, 16).replace('T', ' ')}</dd></>)}
                {detail.financeApprovedAt && (<><dt>재정부 승인</dt><dd>{detail.financeApprovedAt.slice(0, 16).replace('T', ' ')}</dd></>)}
                {detail.paidDate && (<><dt>지급일</dt><dd>{detail.paidDate} · 지출 내역 #{detail.paidRecordId}</dd></>)}
              </dl>

              <div className="fin-attachments">
                <div className="fin-attachments-head">
                  <b>증빙 영수증 ({detail.attachments?.length ?? 0})</b>
                  {writable && detail.status !== 'paid' && (
                    <>
                      <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple hidden onChange={(e) => uploadMore(e.target.files)} />
                      <button type="button" className="manage-btn manage-btn--sm" onClick={() => fileRef.current?.click()}>+ 첨부 (사진·PDF)</button>
                    </>
                  )}
                </div>
                {(detail.attachments ?? []).length === 0 && <p className="fin-muted">첨부된 증빙이 없습니다.</p>}
                {(detail.attachments ?? []).map((a) => (
                  <div className="fin-attachment" key={a.id}>
                    <a href={financeFileUrl(ctx, `/expense-requests/attachments/${a.id}`)} target="_blank" rel="noreferrer">
                      {a.mimeType === 'application/pdf' ? '📄' : '🖼️'} {a.originalName}
                    </a>
                    <span className="fin-muted">{Math.ceil(a.size / 1024)}KB</span>
                    {writable && detail.status !== 'paid' && (
                      <button type="button" className="fin-linkbtn fin-danger" onClick={() => removeAttachment(a.id)}>삭제</button>
                    )}
                  </div>
                ))}
              </div>

              {payForm && (
                <div className="fin-step">
                  <b>지급 완료 처리</b>
                  <div className="manage-form manage-form--grid">
                    <label className="manage-field"><span>지급일</span>
                      <input type="date" value={payForm.paidDate} onChange={(e) => setPayForm({ ...payForm, paidDate: e.target.value })} />
                    </label>
                    <label className="manage-field"><span>지급 방법</span>
                      <select value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })}>
                        {withCurrent(PAYMENT_METHODS, payForm.method).map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </label>
                  </div>
                  <div className="fin-actions">
                    <button type="button" className="manage-btn" onClick={() => setPayForm(null)}>취소</button>
                    <button type="button" className="manage-btn manage-btn--primary"
                      onClick={() => act('/pay', payForm).then((ok) => ok && setPayForm(null))}>지급 완료 · 지출 내역 기록</button>
                  </div>
                </div>
              )}
              {actionError && <div className="fin-inline-error">{actionError}</div>}
              {detail.status === 'dept_approved' && !isManager(ctx) && (
                <p className="fin-muted">재정부 승인은 재정부장만 할 수 있습니다.</p>
              )}
            </div>
            <div className="manage-modal-footer fin-footer-split">
              <div>
                <button type="button" className="manage-btn" onClick={() => setPrinting(detail)}>🖨️ 결의서 인쇄</button>
                {writable && detail.status !== 'paid' && (
                  <button type="button" className="manage-btn manage-btn--danger" onClick={removeRequest}>삭제</button>
                )}
              </div>
              {writable && !payForm && (
                <div>
                  {(detail.status === 'requested' || detail.status === 'rejected') && (
                    <button type="button" className="manage-btn" onClick={() => openEdit(detail)}>
                      {detail.status === 'rejected' ? '수정 후 재청구' : '수정'}
                    </button>
                  )}
                  {(detail.status === 'requested' || (isManager(ctx) && ['dept_approved', 'finance_approved'].includes(detail.status))) && (
                    <button type="button" className="manage-btn" onClick={reject}>반려</button>
                  )}
                  {detail.status === 'requested' && (
                    <button type="button" className="manage-btn manage-btn--primary" onClick={deptApprove}>부서장 승인 기록</button>
                  )}
                  {detail.status === 'dept_approved' && isManager(ctx) && (
                    <button type="button" className="manage-btn manage-btn--primary" onClick={() => act('/finance-approve')}>재정부 승인</button>
                  )}
                  {detail.status === 'finance_approved' && (
                    <button type="button" className="manage-btn manage-btn--primary"
                      onClick={() => setPayForm({ paidDate: todayValue(), method: detail.payMethod || '계좌이체' })}>지급 완료 처리</button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {form && (
        <div className="manage-modal-overlay" onClick={() => setForm(null)}>
          <div className="manage-modal fin-modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="manage-modal-header">
              <h3>{editingId ? '결의서 수정' : '지출 결의서 작성'}</h3>
              <button type="button" className="manage-modal-close" onClick={() => setForm(null)}>×</button>
            </div>
            <div className="manage-modal-body">
              <div className="manage-form manage-form--grid">
                <label className="manage-field"><span>청구일</span>
                  <input type="date" value={form.requestDate} onChange={(e) => setForm({ ...form, requestDate: e.target.value })} />
                </label>
                <label className="manage-field"><span>청구 부서</span>
                  <select value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}>
                    <option value="">공통</option>
                    {withCurrent(departments, form.department).map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </label>
                <label className="manage-field manage-field--full"><span>제목</span>
                  <input value={form.title} placeholder="예: 여름수련회 간식비" onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </label>
                <label className="manage-field"><span>계정과목</span>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    {withCurrent(expenseCategories, form.category).map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                <label className="manage-field"><span>금액</span>
                  <input inputMode="numeric" value={form.amount} placeholder="원"
                    onChange={(e) => setForm({ ...form, amount: e.target.value.replace(/[^\d]/g, '') })} />
                  {form.amount && <small className="fin-hint">{won(Number(form.amount))}</small>}
                </label>
                <label className="manage-field"><span>청구자</span>
                  <input value={form.requesterName} onChange={(e) => setForm({ ...form, requesterName: e.target.value })} />
                </label>
                <label className="manage-field"><span>지급처(받는 분)</span>
                  <input value={form.payee} onChange={(e) => setForm({ ...form, payee: e.target.value })} />
                </label>
                <label className="manage-field"><span>지급 방법</span>
                  <select value={form.payMethod} onChange={(e) => setForm({ ...form, payMethod: e.target.value })}>
                    {withCurrent(PAYMENT_METHODS, form.payMethod).map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </label>
                <label className="manage-field"><span>입금 계좌</span>
                  <input value={form.bankAccount} placeholder="은행 · 계좌번호 · 예금주" onChange={(e) => setForm({ ...form, bankAccount: e.target.value })} />
                </label>
                <label className="manage-field manage-field--full"><span>청구 내용</span>
                  <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
                </label>
                {!editingId && (
                  <label className="manage-field manage-field--full"><span>증빙 영수증 (사진·PDF, 최대 5개)</span>
                    <input type="file" accept="image/*,application/pdf" multiple
                      onChange={(e) => setPendingFiles(Array.from(e.target.files ?? []).slice(0, 5))} />
                  </label>
                )}
              </div>
              {formError && <div className="fin-inline-error">{formError}</div>}
            </div>
            <div className="manage-modal-footer">
              <button type="button" className="manage-btn" onClick={() => setForm(null)}>취소</button>
              <button type="button" className="manage-btn manage-btn--primary" onClick={submitForm} disabled={saving}>
                {saving ? '저장 중...' : editingId ? '저장' : '청구하기'}
              </button>
            </div>
          </div>
        </div>
      )}

      {printing && (
        <PrintPortal title={`지출결의서_${printing.id}`} onDone={() => setPrinting(null)}>
          <ExpenseRequestPrint request={printing} />
        </PrintPortal>
      )}
    </div>
  );
}
